import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, CheckCircle2 } from 'lucide-react';
import { getQuestionTypeChipStyles, getQuestionTypeOptions } from './questionBankUtils';

export default function QuestionBankFormFields({ form, setForm }) {
  const { t } = useTranslation();
  const questionTypeOptions = getQuestionTypeOptions(t);

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-gray-800 mb-2.5 font-semibold text-sm">
          {t('jobDescription.questionType', 'Question Type')}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {questionTypeOptions.map((type) => {
            const selected = form.question_type === type.value;
            return (
              <button
                type="button"
                key={type.value}
                onClick={() => setForm((prev) => ({ ...prev, question_type: type.value }))}
                className={`px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${getQuestionTypeChipStyles(type.value, selected)}`}
              >
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-gray-800 mb-2.5 font-semibold text-sm">
          {t('jobDescription.question', 'Enter interview question')}
        </label>
        <textarea
          rows={4}
          className="w-full p-3.5 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none resize-none text-sm bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
          placeholder={t('jobDescription.questionPlaceholder', 'Enter the interview question')}
          value={form.text}
          onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
        />
      </div>

      {form.question_type === 'coding' && (
        <div className="p-4 rounded-2xl border-2 border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50">
          <label className="flex items-center gap-2 text-orange-800 mb-2.5 font-semibold text-sm">
            <Clock size={16} />
            {t('jobDescription.timeLimit', 'Time Limit (minutes)')}
          </label>
          <input
            type="number"
            min={1}
            max={60}
            className="w-full sm:w-40 p-3 border-2 border-orange-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm bg-white"
            value={form.time_limit}
            onChange={(e) => setForm((prev) => ({
              ...prev,
              time_limit: Math.min(60, Math.max(1, parseInt(e.target.value, 10) || 5)),
            }))}
          />
          <p className="text-xs text-orange-700/80 mt-2">
            {t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}
          </p>
        </div>
      )}

      {form.question_type === 'mcq' && (
        <div className="p-4 rounded-2xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 space-y-3">
          <p className="text-sm font-semibold text-emerald-800">
            {t('jobDescription.multipleChoiceOptions', 'Multiple Choice Options')}
          </p>
          <p className="text-xs text-emerald-700/80 -mt-1">
            {t('questionBank.markCorrectHint', 'Select the radio button to mark the correct answer.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form.mcqOptions.map((opt, i) => {
              const isCorrect = form.mcqCorrectIndex === i;
              return (
                <label
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    isCorrect
                      ? 'bg-white border-emerald-400 shadow-sm'
                      : 'bg-white/70 border-emerald-100 hover:border-emerald-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="question-bank-mcq-correct"
                    checked={isCorrect}
                    onChange={() => setForm((prev) => ({ ...prev, mcqCorrectIndex: i }))}
                    className="accent-emerald-600"
                    aria-label={`Mark answer ${String.fromCharCode(65 + i)} correct`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-gray-500">
                        {t('jobDescription.answer', 'Answer')} {String.fromCharCode(65 + i)}
                      </span>
                      {isCorrect && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                          <CheckCircle2 size={12} />
                          {t('questionBank.correct', 'Correct')}
                        </span>
                      )}
                    </div>
                    <input
                      className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 outline-none text-sm"
                      placeholder={`${t('jobDescription.answer', 'Answer')} ${String.fromCharCode(65 + i)}`}
                      value={opt}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        mcqOptions: prev.mcqOptions.map((value, idx) => (idx === i ? e.target.value : value)),
                      }))}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
